import { useState } from 'react'
import { useForm, type Resolver } from 'react-hook-form'
import { zodResolverI18n } from '@/lib/zod-resolver-i18n'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import {
	Form, FormControl, FormDialog, FormField, FormItem, FormLabel, FormMessage,
	Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@rentflowdev/ui'
import { useUpdateRentalMutation } from '@/api'
import { CurrencyField } from '@/components/currency-field'
import { VatSummary } from '@/components/vat-summary'
import { extractErrorMessage } from '@/lib/error-message'
import { RENTAL_PERIODS } from '@/types'
import type { Rental } from '@/types'
import { optionalNumber } from '../add-property-dialog/schema'

const schema = z.object({
	startDate:  z.string().min(1),
	endDate:    z.string().optional(),
	rentAmount: z.coerce.number().positive(),
	// No currency here: the picker is gone and the rental keeps the currency
	// it was signed in. Validating the stored value would silently block Save
	// for any record outside today's supported list.
	period:     z.enum(['WEEKLY', 'MONTHLY', 'QUARTERLY', 'YEARLY']),
	vatIncluded:      z.boolean(),
	depositAmount:    optionalNumber(z.coerce.number().min(0)),
	penaltyRate:      optionalNumber(z.coerce.number().min(0)),
	penaltyGraceDays: optionalNumber(z.coerce.number().int().min(0)),
}).superRefine((values, ctx) => {
	// Mirrors the rental-service check, so the message lands under the field
	// instead of arriving as a toast from a rejected gRPC call.
	if (values.endDate && values.endDate <= values.startDate) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			path: ['endDate'],
			message: 'rent.startDialog.validation.endBeforeStart',
		})
	}
})
type FormValues = z.infer<typeof schema>

interface EditRentalDialogProps {
	rental:       Rental
	open:         boolean
	onOpenChange: (open: boolean) => void
}

export function EditRentalDialog({ rental, open, onOpenChange }: EditRentalDialogProps) {
	const { t } = useTranslation()
	const [updateRental, { isLoading }] = useUpdateRentalMutation()
	const [errorMsg, setErrorMsg] = useState<string | null>(null)

	const form = useForm<FormValues>({
		// Cast needed: schema uses z.preprocess()/coerce, so zod's Input != Output.
		resolver: zodResolverI18n(schema) as Resolver<FormValues>,
		values: {
			startDate:  rental.startDate.slice(0, 10),
			endDate:    rental.endDate?.slice(0, 10) ?? '',
			rentAmount: Number(rental.rentAmount),
			period:     rental.period as FormValues['period'],
			vatIncluded:      rental.vatIncluded ?? true,
			depositAmount:    rental.depositAmount    != null ? Number(rental.depositAmount)    : undefined,
			penaltyRate:      rental.penaltyRate      != null ? Number(rental.penaltyRate)      : undefined,
			penaltyGraceDays: rental.penaltyGraceDays != null ? Number(rental.penaltyGraceDays) : undefined,
		},
	})

	async function onSubmit(values: FormValues) {
		setErrorMsg(null)
		try {
			await updateRental({
				id:   rental.id,
				body: {
					startDate:  values.startDate,
					endDate:    values.endDate || undefined,
					rentAmount: String(values.rentAmount),
					period:     values.period,
					vatIncluded:      values.vatIncluded,
					depositAmount:    values.depositAmount    != null ? String(values.depositAmount)    : undefined,
					penaltyRate:      values.penaltyRate      != null ? String(values.penaltyRate)      : undefined,
					penaltyGraceDays: values.penaltyGraceDays,
				},
			}).unwrap()
			onOpenChange(false)
		} catch (err) {
			setErrorMsg(extractErrorMessage(err) ?? null)
		}
	}

	return (
		<Form {...form}>
			<FormDialog
				open={open}
				onOpenChange={onOpenChange}
				title={t('rent.editDialog.title')}
				onSubmit={form.handleSubmit(onSubmit)}
				submitLabel={isLoading ? t('common.saving') : t('common.save')}
				cancelLabel={t('common.cancel')}
				closeAriaLabel={t('common.close')}
				isSubmitting={isLoading}
			>
				<div className="grid grid-cols-2 gap-3">
					<FormField
						control={form.control}
						name="startDate"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('rent.startDialog.startDate')}</FormLabel>
								<FormControl>
									<Input type="date" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="endDate"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('rent.startDialog.endDate')}</FormLabel>
								<FormControl>
									<Input type="date" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<div className="grid grid-cols-3 gap-3">
					<FormField
						control={form.control}
						name="rentAmount"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{t('rent.editDialog.rentAmount')}
								</FormLabel>
								<FormControl>
									<Input type="number" min={0} {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					{/* The rental keeps the currency it was signed in — an agreed sum
					    is not re-denominated from a settings screen. */}
					<CurrencyField label={t('rent.editDialog.currency')} value={rental.currency} />
					<FormField
						control={form.control}
						name="period"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('rent.table.period')}</FormLabel>
								<Select onValueChange={field.onChange} value={field.value}>
									<FormControl>
										<SelectTrigger className="h-auto px-4 py-[10px] text-sm">
											<SelectValue />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{RENTAL_PERIODS.map((p) => (
											<SelectItem key={p} value={p}>
												{t(`rent.periods.${p.toLowerCase()}`, { defaultValue: p })}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				{/* The rate was frozen when the rental was created and is not
				    re-derived here: invoices already issued rest on it. Only the
				    split between price and VAT is still a term to agree on. */}
				{rental.vatPercent != null && (
					<div className="flex flex-col gap-1">
						{/* Через FormField, как остальные поля: `vatIncluded` — поле схемы,
						    и обходить примитивы ради переключателя внутри сводки незачем. */}
						<FormField
							control={form.control}
							name="vatIncluded"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<VatSummary
											snapshot={{ percent: Number(rental.vatPercent), reason: rental.vatReason }}
											amount={form.watch('rentAmount')}
											included={field.value}
											onIncludedChange={(next) => field.onChange(next)}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<p className="text-xs text-(--color-muted-foreground)">
							{t('rent.editDialog.vatSnapshot')}
						</p>
					</div>
				)}

				<div className="grid grid-cols-2 gap-3">
					<FormField
						control={form.control}
						name="depositAmount"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{t('rent.editDialog.depositAmount')}
								</FormLabel>
								<FormControl>
									<Input type="number" min={0} {...field} value={field.value ?? ''} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="penaltyRate"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{t('rent.editDialog.penaltyRate')}
								</FormLabel>
								<FormControl>
									<Input type="number" min={0} step={0.01} {...field} value={field.value ?? ''} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="penaltyGraceDays"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{t('rent.editDialog.penaltyGraceDays')}
								</FormLabel>
								<FormControl>
									<Input type="number" min={0} {...field} value={field.value ?? ''} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				{errorMsg && (
					<div className="rounded-(--radius-md) border border-(--color-destructive)/30 bg-(--color-destructive)/5 px-3 py-2">
						<p className="text-sm text-(--color-destructive)">{errorMsg}</p>
					</div>
				)}
			</FormDialog>
		</Form>
	)
}
